import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
    setAccessToken,
    setRefreshToken,
    getAccessToken,
    getRefreshToken,
    clearAuthSession,
} from '@/lib/authSession';
import { mergeAccessTokenClaims } from '@/features/auth/utils/accessTokenClaims';

import { API_BASE_URL } from '@/lib/apiOrigin';
import { loginErrorMessage } from '@/features/auth/utils/loginErrorMessage';


/**
 * Login: POST /auth/login, then GET /auth/me so Redux holds the same profile as a restored session.
 */
export const loginUser = createAsyncThunk(
    'auth/login',
    async (credentials, { rejectWithValue }) => {
        let tokensWritten = false;
        try {
            setAccessToken(null);
            const { default: apiClient } = await import('@/lib/axios');
            const { ENDPOINTS } = await import('@/services/api/endpoints');

            // Trimmed on both fields: a leading/trailing space around either is a
            // typing or paste artefact, never part of the credential.
            const userName = String(credentials.userName ?? credentials.username ?? '').trim();
            const { data: loginRes } = await apiClient.post(ENDPOINTS.AUTH.LOGIN, {
                userName,
                password: String(credentials.password ?? '').trim(),
            });

            const accessToken = loginRes.tokens?.access?.token;
            const refreshToken = loginRes.tokens?.refresh?.token;
            if (!loginRes.account?.id || !loginRes.account?.user?.id || !accessToken) {
                // A 200 that is missing the account or token is a server-side problem,
                // not a bad credential — report it as such rather than blaming the user.
                return rejectWithValue(loginErrorMessage({ status: 500 }));
            }

            setAccessToken(accessToken);
            if (refreshToken) setRefreshToken(refreshToken);
            tokensWritten = true;

            const { data: profileRes } = await apiClient.get(ENDPOINTS.AUTH.ME);
            const account = profileRes.account;
            if (!account?.user?.id) {
                clearAuthSession();
                return rejectWithValue(loginErrorMessage({ status: 500 }));
            }

            // Prefer API `tokens.access.permissions`, then JWT — so guards work even if /me omits roles or decode fails.
            return mergeAccessTokenClaims(
                account,
                accessToken,
                loginRes.tokens?.access?.permissions,
            );
        } catch (error) {
            if (tokensWritten) clearAuthSession();
            // Never surface raw API/axios text on the sign-in screen.
            return rejectWithValue(loginErrorMessage(error));
        }
    }
);

/**
 * Restore session after full reload: refresh token in sessionStorage → new access → GET /auth/me.
 */
export const restoreSession = createAsyncThunk('auth/restoreSession', async (_, { rejectWithValue }) => {
    try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
            return rejectWithValue('Not authenticated');
        }
        const { data } = await axios.post(`${API_BASE_URL}/auth/refreshToken`, { refreshToken });
        const access = data.access?.token;
        const nextRt = data.refresh?.token;
        if (!access) {
            clearAuthSession();
            return rejectWithValue('Invalid refresh response');
        }
        setAccessToken(access);
        if (nextRt) setRefreshToken(nextRt);

        const { default: apiClient } = await import('@/lib/axios');
        const { ENDPOINTS } = await import('@/services/api/endpoints');
        const { data: meRes } = await apiClient.get(ENDPOINTS.AUTH.ME);
        const account = meRes.account;
        if (!account?.user?.id) {
            clearAuthSession();
            return rejectWithValue('Invalid profile response');
        }
        return mergeAccessTokenClaims(account, access, data.access?.permissions);
    } catch (error) {
        clearAuthSession();
        return rejectWithValue(error.message || 'Session restore failed');
    }
});

/**
 * Load account for an existing session (access token in memory).
 */
export const fetchCurrentUser = createAsyncThunk(
    'auth/fetchCurrentUser',
    async (_, { rejectWithValue }) => {
        try {
            if (!getAccessToken()) {
                return rejectWithValue('Not authenticated');
            }
            const { default: apiClient } = await import('@/lib/axios');
            const { ENDPOINTS } = await import('@/services/api/endpoints');
            const { data } = await apiClient.get(ENDPOINTS.AUTH.ME);
            const account = data.account;
            if (!account?.user?.id) {
                return rejectWithValue('Invalid profile response');
            }
            const token = getAccessToken();
            return mergeAccessTokenClaims(account, token);
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch user');
        }
    }
);

/**
 * Clear the client session first so hooks (e.g. chat) stop calling the API, then notify the server.
 * If we awaited server logout before clearing storage, the JWT was already invalid while Redux still
 * had a user — chat effects would fire and produce 401s.
 */
export const performLogout = createAsyncThunk('auth/performLogout', async (_, { dispatch }) => {
    const accessToken = getAccessToken();
    dispatch({ type: 'auth/logout' });
    try {
        if (accessToken) {
            const { default: apiClient } = await import('@/lib/axios');
            const { ENDPOINTS } = await import('@/services/api/endpoints');
            await apiClient.post(
                ENDPOINTS.AUTH.LOGOUT,
                {},
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
        }
    } catch {
        /* session already cleared */
    }
});

const initialState = {
    user: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout(state) {
            state.user = null;
            state.isAuthenticated = false;
            state.error = null;
            clearAuthSession();
        },
        clearAuthError(state) {
            state.error = null;
        },
        /**
         * After axios refresh (or any new access token), re-merge JWT `permissions` / `roles` into session user.
         */
        syncSessionFromAccessToken(state, action) {
            const token = action.payload;
            if (!state.user || typeof token !== 'string' || !token) return;
            state.user = mergeAccessTokenClaims(state.user, token);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loginUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.user = action.payload;
                state.isAuthenticated = true;
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(restoreSession.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(restoreSession.fulfilled, (state, action) => {
                state.isLoading = false;
                state.user = action.payload;
                state.isAuthenticated = true;
            })
            .addCase(restoreSession.rejected, (state) => {
                state.isLoading = false;
                state.user = null;
                state.isAuthenticated = false;
                clearAuthSession();
            })
            .addCase(fetchCurrentUser.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchCurrentUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.user = action.payload;
                state.isAuthenticated = true;
            })
            .addCase(fetchCurrentUser.rejected, (state) => {
                state.isLoading = false;
                // Keep existing session on transient /me failure (e.g. race after login) if we already have a profile.
                if (state.user?.user?.id) {
                    return;
                }
                state.user = null;
                state.isAuthenticated = false;
                clearAuthSession();
            })
            .addCase(performLogout.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(performLogout.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(performLogout.rejected, (state) => {
                state.isLoading = false;
            });
    },
});

export const { logout, clearAuthError, syncSessionFromAccessToken } = authSlice.actions;
export const authReducer = authSlice.reducer;
